import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import maplibregl, { Map as MlMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { apiDelete, apiGet, apiPost, apiPut } from "../api/client";
import ChatMarkdown from "../components/ChatMarkdown";
import InterestToggle, { type Interest } from "../components/InterestToggle";
import {
  OFFER_STATUSES,
  OfferForm,
  SecondaryButton,
  Stars,
  VisitAnswerChips,
  VisitForm,
  fmtVisitDate,
  type Offer,
  type OfferDraft,
  type Visit,
  type VisitDraft,
} from "../components/VisitForm";

interface SchoolScores {
  elementary?: number | null;
  middle?: number | null;
  high?: number | null;
}

interface SchoolNames {
  elementary?: string | null;
  middle?: string | null;
  high?: string | null;
}

interface PriceHistoryEntry {
  date: string;
  price: number;
  event: string;
}

interface Generation {
  request_id: string;
  status: "queued" | "running" | "retrying" | "succeeded" | "failed";
  stage: string;
  requested_at: string;
  updated_at: string;
  attempt: number;
  error?: string;
}

interface DisclosureItem {
  topic: string;
  question: string;
  answer: "yes" | "no" | "unknown" | "not_answered" | "see_notes";
  detail: string;
  page: number | null;
  concern: boolean;
}

interface DisclosureNotes {
  form: string;
  completed_on: string | null;
  seller_occupancy: string;
  items: DisclosureItem[];
  limitations: string[];
}

interface Listing {
  listing_id: string;
  source: string;
  url: string;
  address: string;
  town: string;
  status: string;
  list_price: number | null;
  sale_price: number | null;
  sale_date: string | null;
  price_history: PriceHistoryEntry[];
  sqft: number | null;
  beds: number | null;
  baths: number | null;
  lot_size: number | null;
  year_built: number | null;
  days_on_zillow: number | null;
  school_scores: SchoolScores;
  school_names?: SchoolNames;
  photos: string[];
  hero_photo?: string;
  lat: number | null;
  lng: number | null;
  first_seen_at: string;
  listed_date?: string | null;
  last_seen_at: string;
  interest?: Interest;
  description?: string;
  ai_summary?: string;
  ai_pros?: string[];
  ai_cons?: string[];
  ai_price_assessment?: "underpriced" | "fair" | "overpriced" | "unknown";
  ai_verdict?: string;
  ai_fair_price_low?: number | null;
  ai_fair_price_high?: number | null;
  ai_confidence?: "low" | "medium" | "high";
  ai_valuation_reasoning?: string;
  ai_offer_guidance?: string;
  ai_verify_before_offer?: string[];
  zestimate?: number | null;
  zestimate_low?: number | null;
  zestimate_high?: number | null;
  tax_assessed_value?: number | null;
  ai_summary_at?: string;
  ai_generation?: Generation;
  ai_model?: string;
  ai_comp_count?: number;
  ai_comp_listing_ids?: string[];
  // Markdown report pasted in from an external deep-research agent.
  research_report?: string;
  research_source?: string;
  research_updated_at?: string;
  // Seller's property condition disclosure the buyer uploaded, and what the
  // analysis read from it.
  disclosure_key?: string;
  disclosure_filename?: string;
  disclosure_size?: number;
  disclosure_uploaded_at?: string;
  ai_disclosure?: DisclosureNotes | null;
  ai_disclosure_extracted_at?: string;
  ai_disclosure_error?: string;
  match_score?: number | null;
  match_score_breakdown?: Record<string, { score: number; weight: number }>;
  garage_spaces?: number | null;
  amenities?: Record<string, boolean>;
  // Present only when a field was backfilled from the county assessment
  // record rather than the listing itself.
  sqft_source?: "modiv" | null;
  lot_size_source?: "modiv" | null;
  year_built_source?: "modiv" | null;
  modiv?: ModivRecord;
  modiv_status?: string | null;
  modiv_checked_at?: string | null;
  // Computed from the listing's coordinates rather than scraped. Both 0-100
  // and higher-is-better: how quiet the house is, and how calm its own street
  // is. See backend lib/streetnoise.py.
  noise_score?: number | null;
  street_busyness?: number | null;
  noise?: NoiseRecord;
  noise_status?: string | null;
  noise_checked_at?: string | null;
  // First Street climate risk via Zillow's extras query ({} = Zillow had none)
  // and FEMA's flood zone at the coordinates. See backend lib/fema_flood.py.
  climate?: Record<string, ClimateHazard> | null;
  flood?: FloodRecord;
  flood_status?: string | null;
  flood_checked_at?: string | null;
}

interface ClimateHazard {
  score?: number;
  max?: number;
  label?: string;
  fema_zone?: string;
  insurance_recommendation?: string;
  insurance_separate_policy?: boolean;
  historic_count?: number;
  probability?: { probability: number; year?: number }[];
}

interface FloodRecord {
  zone?: string;
  subtype?: string;
  sfha?: boolean;
  insurance_required?: boolean;
  base_flood_elevation_ft?: number;
  nearby_sfha_zones?: string[];
  sfha_within_m?: number;
  panel?: string;
}

interface NoiseSource {
  name?: string;
  highway?: string;
  railway?: string;
  distance_m?: number;
  aadt?: number | null;
  aadt_source?: "njdot" | "class_prior";
  aadt_year?: number | null;
  db?: number;
}

interface NoiseRecord {
  noise_db?: number;
  noise_band?: string;
  busyness_band?: string;
  frontage?: NoiseSource;
  sources?: NoiseSource[];
  rail?: NoiseSource | null;
  industrial_m?: number | null;
  aerodrome?: { name?: string; distance_m?: number } | null;
  context_penalty?: number;
  aadt_measured?: number;
  howloud?: { soundscore?: number };
  elements_seen?: number;
}

interface ModivRecord {
  square_ft?: number | null;
  year_built?: number | null;
  acreage?: number | null;
  property_class?: string;
  style?: string;
  bldg_desc?: string;
  land_desc?: string;
  zone?: string;
  district?: string;
  district_town?: string;
  block?: string;
  lot?: string;
  matched_location?: string;
  detail_url?: string;
  last_sale_date?: string;
  last_sale_price?: number | null;
  assessment_updated?: string;
  search_term?: string;
  candidate_count?: number;
  matches_in_other_towns?: number;
  reason?: string;
}

export default function ListingDetail() {
  const { listingId } = useParams();
  const q = useQuery<Listing>({
    queryKey: ["listing", listingId],
    queryFn: () => apiGet(`/api/listings/${listingId}`),
    enabled: !!listingId,
    refetchInterval: (query) => generationActive(query.state.data?.ai_generation) ? 5000 : false,
  });

  if (q.isLoading) return <p className="muted">Loading…</p>;
  if (!q.data) return <p className="tag-error">Failed to load listing.</p>;

  const l = q.data;
  const photoUrls = photoSrcList(l);
  const enriched = (l.photos?.length ?? 0) > 0 || (l.price_history?.length ?? 0) > 0 ||
    !!l.school_scores?.elementary || !!l.school_scores?.middle || !!l.school_scores?.high;

  return (
    <>
      {q.isRefetchError && <p role="alert" className="tag-error">
        Unable to check generation progress. The last known status is shown below.
        <button onClick={() => { void q.refetch(); }}>Check again</button>
      </p>}
      <PhotoGallery photos={photoUrls} />
      <Header listing={l} />
      <StatsBar listing={l} />
      {!enriched && (
        <div className="panel" style={{ borderColor: "var(--warn)" }}>
          <p className="muted" style={{ margin: 0 }}>
            ⏳ Detail data not yet ingested for this listing. Photos, school scores, and price
            history will populate after the next sweep enriches it. Showing summary fields only.
          </p>
        </div>
      )}
      <AISummaryPanel listing={l} />
      <DisclosurePanel listing={l} />
      <ResearchPanel listing={l} />
      <MapPanel listing={l} />
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div>
          {l.description && <DescriptionPanel description={l.description} />}
          <OfferPanel listing={l} />
          <VisitsPanel listingId={l.listing_id} />
          <PriceHistoryPanel history={l.price_history ?? []} />
        </div>
        <div>
          <MatchScorePanel listing={l} />
          <FactsPanel listing={l} />
          <ValuationPanel listing={l} />
          <SchoolsPanel scores={l.school_scores ?? {}} names={l.school_names ?? {}} />
          <FloodRiskPanel listing={l} />
          <StreetNoisePanel listing={l} />
          <TaxRecordPanel listing={l} />
        </div>
      </div>
    </>
  );
}

function photoSrcList(l: Listing): string[] {
  // photos[] holds either S3 keys (after photo-download) or full URLs.
  // S3 keys look like "photos/zillow:zpid/00.jpg" — serve them via the SPA's
  // CloudFront /photos/* behaviour. Hero photo may be a direct CDN URL.
  const out: string[] = [];
  for (const p of l.photos ?? []) {
    out.push(p.startsWith("http") ? p : `/${p}`);
  }
  if (out.length === 0 && l.hero_photo) out.push(l.hero_photo);
  return out;
}

function PhotoGallery({ photos }: { photos: string[] }) {
  const [idx, setIdx] = useState(0);
  if (photos.length === 0) {
    return (
      <div
        className="panel"
        style={{ height: 340, display: "grid", placeItems: "center", color: "var(--muted)" }}
      >
        No photos yet
      </div>
    );
  }
  return (
    <div className="panel" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ position: "relative", aspectRatio: "16 / 9", background: "var(--bg)" }}>
        <img
          src={photos[idx]}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {photos.length > 1 && (
          <>
            <NavBtn dir="prev" onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)} />
            <NavBtn dir="next" onClick={() => setIdx((i) => (i + 1) % photos.length)} />
            <div
              style={{
                position: "absolute",
                bottom: 8,
                right: 12,
                background: "rgba(0,0,0,0.6)",
                padding: "3px 9px",
                borderRadius: 12,
                fontSize: 12,
              }}
            >
              {idx + 1} / {photos.length}
            </div>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div style={{ display: "flex", gap: 4, padding: 6, overflowX: "auto", background: "var(--bg)" }}>
          {photos.map((p, i) => (
            <img
              key={p + i}
              src={p}
              alt=""
              onClick={() => setIdx(i)}
              style={{
                width: 80,
                height: 60,
                objectFit: "cover",
                borderRadius: 4,
                cursor: "pointer",
                outline: i === idx ? "2px solid var(--accent)" : "none",
                opacity: i === idx ? 1 : 0.7,
                flexShrink: 0,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NavBtn({ dir, onClick }: { dir: "prev" | "next"; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={dir}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        [dir === "prev" ? "left" : "right"]: 12,
        background: "rgba(0,0,0,0.6)",
        color: "white",
        border: 0,
        width: 36,
        height: 36,
        borderRadius: 18,
        fontSize: 16,
        cursor: "pointer",
      } as React.CSSProperties}
    >
      {dir === "prev" ? "‹" : "›"}
    </button>
  );
}

function generationActive(run?: Generation) {
  return !!run && ["queued", "running", "retrying"].includes(run.status);
}

function useRegeneration(l: Listing) {
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());
  const active = generationActive(l.ai_generation);
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active, l.ai_generation?.request_id]);
  const mutation = useMutation({
    mutationFn: () => apiPost<{ generation: Generation }>(`/api/listings/${l.listing_id}/resummarize`),
    onSuccess: (result) => {
      qc.setQueryData<Listing>(["listing", l.listing_id], (old) => old
        ? { ...old, ai_generation: result.generation } : old);
      void qc.invalidateQueries({ queryKey: ["listing", l.listing_id] });
    },
  });
  const seconds = Math.max(0, Math.floor((now - Date.parse(l.ai_generation?.requested_at || "")) / 1000)) || 0;
  const elapsed = `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const stalled = active && now - Date.parse(l.ai_generation!.updated_at) > 6 * 60 * 1000;
  const stages: Record<string, string> = {
    queued: "Queued — waiting to start", starting: "Starting analysis",
    finding_comps: "Finding comparable homes", reviewing_photos: "Reviewing photo evidence",
    reviewing_comp_photos: "Grading comparable homes' condition",
    reading_disclosure: "Reading the seller's disclosure",
    valuing: "Writing valuation and summary", judging_fit: "Judging fit for your search", retrying: "Waiting for automatic retry",
  };
  return { mutation, active, stalled, elapsed, stage: stages[l.ai_generation?.stage || ""] || "Generating" };
}

function AISummaryPanel({ listing: l }: { listing: Listing }) {
  const { mutation, active, stalled, elapsed, stage } = useRegeneration(l);

  const has = !!(l.ai_summary || l.ai_verdict);
  const assessmentColor: Record<string, string> = {
    underpriced: "var(--good)",
    fair: "var(--accent)",
    overpriced: "var(--bad)",
    unknown: "var(--muted)",
  };

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>AI summary</h3>
        <span style={{ flex: 1 }} />
        {l.ai_summary_at && (
          <span className="muted" style={{ fontSize: 11 }}>
            {fmtDate(l.ai_summary_at)} · {l.ai_model?.split(".").slice(-1)[0] || l.ai_model}
          </span>
        )}
        <button onClick={() => mutation.mutate()} disabled={mutation.isPending || (active && !stalled)} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text)" }}>
          {mutation.isPending ? "Queuing…" : active && !stalled ? `${stage}…` : stalled || l.ai_generation?.status === "failed" ? "Retry generation" : has ? "Regenerate" : "Generate"}
        </button>
      </div>

      {mutation.isError && (
        <p role="alert" style={{ margin: "0 0 8px", color: "var(--bad)" }}>
          {mutation.error.message}
        </p>
      )}

      {!has && !l.ai_generation && (
        <p className="muted" style={{ margin: 0 }}>
          No AI summary yet. Click Generate to start analysis.
        </p>
      )}
      {l.ai_generation && (
        <p role={l.ai_generation.status === "failed" ? "alert" : "status"} aria-live="polite" style={{ margin: "0 0 8px", color: l.ai_generation.status === "failed" ? "var(--bad)" : "var(--muted)" }}>
          {stalled ? "No progress reported for over 6 minutes. This run may be delayed or stopped; you can retry." : l.ai_generation.status === "failed" ? l.ai_generation.error || "Generation failed. Please retry." : l.ai_generation.status === "succeeded" ? "Summary updated successfully." : `${stage} · ${elapsed} elapsed${l.ai_generation.attempt > 1 ? ` · attempt ${l.ai_generation.attempt} of 2` : ""}. The summary will update automatically.`}
          {active && has && " The previous summary remains below until the new one is ready."}
        </p>
      )}

      {has && (
        <>
          {(l.ai_verdict || l.ai_price_assessment) && (
            <p style={{ margin: "0 0 10px", fontStyle: "italic", lineHeight: 1.4 }}>
              <span
                style={{
                  display: "inline-block",
                  background: assessmentColor[l.ai_price_assessment || "unknown"],
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: 11,
                  fontStyle: "normal",
                  marginRight: 8,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                {l.ai_price_assessment ?? "unknown"}
              </span>
              {l.ai_verdict}
            </p>
          )}
          <FairPriceLine
            low={l.ai_fair_price_low ?? null}
            high={l.ai_fair_price_high ?? null}
            currentPrice={l.list_price ?? l.sale_price ?? null}
            confidence={l.ai_confidence}
            reasoning={l.ai_valuation_reasoning}
          />
          {l.ai_offer_guidance && (
            <p style={{ margin: "0 0 12px", lineHeight: 1.5 }}>
              <strong>Offer guidance:</strong> {l.ai_offer_guidance}
            </p>
          )}

          {l.ai_summary && (
            <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, margin: "0 0 12px" }}>
              {l.ai_summary}
            </p>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <ProsConsList title="Pros" items={l.ai_pros ?? []} color="var(--good)" />
            <ProsConsList title="Cons" items={l.ai_cons ?? []} color="var(--bad)" />
          </div>
          {(l.ai_verify_before_offer?.length ?? 0) > 0 && (
            <div style={{ marginTop: 12 }}>
              <ProsConsList title="Verify before offering" items={l.ai_verify_before_offer ?? []} color="var(--warn)" />
            </div>
          )}
          {l.ai_comp_listing_ids && l.ai_comp_listing_ids.length > 0 ? (
            <CompsUsed
              ids={l.ai_comp_listing_ids}
              consideredCount={l.ai_comp_count ?? null}
            />
          ) : l.ai_comp_count != null ? (
            <p className="muted" style={{ marginTop: 10, fontSize: 11 }}>
              Based on {l.ai_comp_count} comparable listing{l.ai_comp_count === 1 ? "" : "s"}.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

const MAX_DISCLOSURE_BYTES = 25 * 1024 * 1024;

const ANSWER_LABEL: Record<DisclosureItem["answer"], string> = {
  yes: "Yes", no: "No", unknown: "Unknown", not_answered: "Not answered", see_notes: "See notes",
};

function DisclosurePanel({ listing: l }: { listing: Listing }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { active } = useRegeneration(l);
  const notes = l.ai_disclosure;
  const items = notes?.items ?? [];
  const flagged = items.filter((i) => i.concern);
  const other = items.filter((i) => !i.concern);

  // Two steps: the PDF goes straight to S3 on a presigned POST (API Gateway
  // caps bodies at 10MB), then the API records it and starts a new analysis.
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.type && file.type !== "application/pdf") throw new Error("Upload a PDF.");
      if (file.size > MAX_DISCLOSURE_BYTES) throw new Error("The PDF is over 25 MB.");
      const target = await apiPost<{ key: string; url: string; fields: Record<string, string> }>(
        `/api/listings/${l.listing_id}/disclosure/upload`,
      );
      const form = new FormData();
      Object.entries(target.fields).forEach(([k, v]) => form.append(k, v));
      form.append("file", file);
      const res = await fetch(target.url, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status}).`);
      return apiPut<{ generation: Generation }>(`/api/listings/${l.listing_id}/disclosure`, {
        key: target.key, filename: file.name,
      });
    },
    onSuccess: (result) => {
      qc.setQueryData<Listing>(["listing", l.listing_id], (old) => old
        ? { ...old, ai_generation: result.generation } : old);
      void qc.invalidateQueries({ queryKey: ["listing", l.listing_id] });
    },
    onSettled: () => { if (fileInput.current) fileInput.current.value = ""; },
  });

  const view = useMutation({
    mutationFn: () => apiGet<{ url: string }>(`/api/listings/${l.listing_id}/disclosure`),
    onSuccess: (r) => { window.open(r.url, "_blank", "noopener,noreferrer"); },
  });

  const remove = useMutation({
    mutationFn: () => apiDelete(`/api/listings/${l.listing_id}/disclosure`),
    onSuccess: () => {
      setConfirmRemove(false);
      void qc.invalidateQueries({ queryKey: ["listing", l.listing_id] });
    },
  });

  const error = upload.error || view.error || remove.error;
  const status = !l.disclosure_key ? null
    : active ? "The analysis reads it during generation; findings appear when it finishes."
    : l.ai_disclosure_error ? `${l.ai_disclosure_error} The summary treats the seller's answers as unknown.`
    : !notes ? "Not read yet. Regenerate the summary to include it."
    : null;

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Seller's disclosure</h3>
        <span style={{ flex: 1 }} />
        {l.disclosure_key && l.disclosure_uploaded_at && (
          <span className="muted" style={{ fontSize: 11 }}>
            {l.disclosure_filename || "disclosure.pdf"} · {fmtDate(l.disclosure_uploaded_at)}
          </span>
        )}
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,.pdf"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }}
        />
        {l.disclosure_key && (
          <SecondaryButton onClick={() => view.mutate()} disabled={view.isPending}>
            {view.isPending ? "Opening…" : "View PDF"}
          </SecondaryButton>
        )}
        <SecondaryButton onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? "Uploading…" : l.disclosure_key ? "Replace" : "Upload PDF"}
        </SecondaryButton>
        {l.disclosure_key && (confirmRemove ? (
          <>
            <SecondaryButton danger onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? "Removing…" : "Confirm remove"}
            </SecondaryButton>
            <SecondaryButton onClick={() => setConfirmRemove(false)}>Cancel</SecondaryButton>
          </>
        ) : (
          <SecondaryButton onClick={() => setConfirmRemove(true)}>Remove</SecondaryButton>
        ))}
      </div>

      {!l.disclosure_key && (
        <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          No disclosure attached. Upload the seller's property condition disclosure (PDF) and the
          analysis reads it once, then uses the seller's answers in the summary, the
          verify-before-offer list and chat.
        </p>
      )}
      {status && <p className="muted" role="status" style={{ margin: "0 0 8px", fontSize: 13 }}>{status}</p>}
      {error && <p role="alert" className="tag-error" style={{ margin: "0 0 8px", fontSize: 12 }}>{error.message}</p>}

      {l.disclosure_key && notes && (
        <>
          <p className="muted" style={{ margin: "0 0 8px", fontSize: 12 }}>
            {[notes.form, notes.completed_on && `completed ${notes.completed_on}`, notes.seller_occupancy]
              .filter(Boolean).join(" · ")}
          </p>
          {flagged.length > 0 ? (
            <DisclosureItems title="Disclosed problems and unknowns" items={flagged} color="var(--warn)" />
          ) : (
            <p style={{ margin: "0 0 8px", fontSize: 13 }}>No problems or unknowns were found on the form.</p>
          )}
          {other.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>
                {other.length} other answer{other.length === 1 ? "" : "s"} on key topics
              </summary>
              <DisclosureItems items={other} color="var(--muted)" />
            </details>
          )}
          {notes.limitations.length > 0 && (
            <p className="muted" style={{ margin: "8px 0 0", fontSize: 11 }}>
              Could not read or not covered: {notes.limitations.join("; ")}
            </p>
          )}
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 11 }}>
            Read by the model from the PDF; check anything important against the original.
          </p>
        </>
      )}
    </div>
  );
}

function DisclosureItems({ title, items, color }: { title?: string; items: DisclosureItem[]; color: string }) {
  return (
    <div>
      {title && <strong style={{ fontSize: 12, color }}>{title}</strong>}
      <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 13, lineHeight: 1.45 }}>
        {items.map((item, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            <span className="muted" style={{ textTransform: "capitalize" }}>{item.topic.replace(/_/g, " ")}</span>
            {" · "}{item.question} <strong>{ANSWER_LABEL[item.answer] ?? item.answer}</strong>
            {item.detail && <> — “{item.detail}”</>}
            {item.page != null && <span className="muted" style={{ fontSize: 11 }}> (p. {item.page})</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResearchPanel({ listing: l }: { listing: Listing }) {
  const qc = useQueryClient();
  const report = (l.research_report ?? "").trim();
  const [showBrief, setShowBrief] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // With no report attached the brief IS the panel, so fetch it up front.
  // Once one exists it's only needed if the user asks to re-run the research.
  const briefWanted = !report || showBrief;
  const brief = useQuery<{ prompt: string }>({
    queryKey: ["research-prompt", l.listing_id],
    queryFn: () => apiGet(`/api/listings/${l.listing_id}/research-prompt`),
    enabled: briefWanted,
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (text: string) =>
      apiPut(`/api/listings/${l.listing_id}/research`, { report: text }),
    onSuccess: () => {
      setDraft("");
      setPasting(false);
      qc.invalidateQueries({ queryKey: ["listing", l.listing_id] });
    },
  });

  const clear = useMutation({
    mutationFn: () => apiDelete(`/api/listings/${l.listing_id}/research`),
    onSuccess: () => {
      setConfirmClear(false);
      qc.invalidateQueries({ queryKey: ["listing", l.listing_id] });
    },
  });

  const { mutation: resummarize, active, stalled, stage } = useRegeneration(l);

  async function copyBrief() {
    const text = brief.data?.prompt;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the brief is on screen and selectable.
      setCopied(false);
    }
  }

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Deep research</h3>
        <span style={{ flex: 1 }} />
        {report && l.research_updated_at && (
          <span className="muted" style={{ fontSize: 11 }}>
            {l.research_source || "external"} · {fmtDate(l.research_updated_at)}
          </span>
        )}
        {report && (
          <>
            <SecondaryButton onClick={() => setShowBrief((s) => !s)}>
              {showBrief ? "Hide brief" : "Show brief"}
            </SecondaryButton>
            <SecondaryButton onClick={() => setPasting((p) => !p)}>Replace</SecondaryButton>
            {confirmClear ? (
              <>
                <SecondaryButton onClick={() => clear.mutate()}>
                  {clear.isPending ? "Clearing…" : "Confirm clear"}
                </SecondaryButton>
                <SecondaryButton onClick={() => setConfirmClear(false)}>Cancel</SecondaryButton>
              </>
            ) : (
              <SecondaryButton onClick={() => setConfirmClear(true)}>Clear</SecondaryButton>
            )}
          </>
        )}
      </div>

      {!report && (
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.5 }}>
          No research attached. Copy the brief below into{" "}
          <a href="https://gemini.google.com/app" target="_blank" rel="noopener noreferrer">
            Gemini Deep Research
          </a>{" "}
          (or any research agent), then paste the report back here. It gets folded into the AI
          summary and the chat context for this listing.
        </p>
      )}

      {briefWanted && (
        <div style={{ marginBottom: report ? 16 : 12 }}>
          <div className="row" style={{ marginBottom: 6 }}>
            <strong style={{ fontSize: 12, color: "var(--muted)" }}>
              Research brief — built from this listing's facts, comps, and prior analysis
            </strong>
            <span style={{ flex: 1 }} />
            <SecondaryButton onClick={copyBrief} disabled={!brief.data?.prompt}>
              {copied ? "Copied ✓" : "Copy brief"}
            </SecondaryButton>
          </div>
          {brief.isLoading && <p className="muted" style={{ margin: 0, fontSize: 12 }}>Building brief…</p>}
          {brief.error && (
            <p className="tag-error" style={{ margin: 0, fontSize: 12 }}>
              Couldn't build the brief: {String(brief.error)}
            </p>
          )}
          {brief.data?.prompt && (
            <pre
              style={{
                margin: 0,
                padding: 12,
                maxHeight: 260,
                overflow: "auto",
                background: "var(--bg)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {brief.data.prompt}
            </pre>
          )}
        </div>
      )}

      {(!report || pasting) && (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Paste the markdown report here…"
            rows={5}
            style={{ width: "100%", fontFamily: "inherit", fontSize: 13, resize: "vertical" }}
          />
          <div className="row" style={{ marginTop: 6 }}>
            <button onClick={() => save.mutate(draft.trim())} disabled={!draft.trim() || save.isPending}>
              {save.isPending ? "Saving…" : "Save report"}
            </button>
            {pasting && <SecondaryButton onClick={() => setPasting(false)}>Cancel</SecondaryButton>}
            <span className="muted" style={{ fontSize: 11 }}>
              {draft.trim() ? `${draft.trim().length.toLocaleString()} characters` : ""}
            </span>
          </div>
          {save.error && (
            <p className="tag-error" style={{ margin: "6px 0 0", fontSize: 12 }}>
              {String(save.error)}
            </p>
          )}
        </div>
      )}

      {report && !pasting && (
        <>
          <div
            style={{
              maxHeight: expanded ? "none" : 420,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <ChatMarkdown text={report} />
            {!expanded && (
              // Fade the cut so it reads as "more below" rather than an abrupt stop.
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 60,
                  background: "linear-gradient(transparent, var(--panel))",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <SecondaryButton onClick={() => setExpanded((e) => !e)}>
              {expanded ? "Collapse" : "Show full report"}
            </SecondaryButton>
            <span style={{ flex: 1 }} />
            <span className="muted" style={{ fontSize: 11 }}>
              {resummarize.isError ? resummarize.error.message : active
                ? "Generation status is shown in the AI summary panel."
                : l.ai_summary_at && l.research_updated_at && l.ai_summary_at >= l.research_updated_at
                  ? "Summary includes the latest research report."
                  : "Regenerate to include this report in the summary."}
            </span>
            <SecondaryButton
              onClick={() => resummarize.mutate()}
              disabled={resummarize.isPending || (active && !stalled)}
            >
              {resummarize.isPending ? "Queuing…" : active && !stalled ? `${stage}…` : "Regenerate summary"}
            </SecondaryButton>
          </div>
        </>
      )}
    </div>
  );
}

function FairPriceLine({
  low,
  high,
  currentPrice,
  confidence,
  reasoning,
}: {
  low: number | null;
  high: number | null;
  currentPrice: number | null;
  confidence?: "low" | "medium" | "high";
  reasoning?: string;
}) {
  if (low == null && high == null) return null;
  const fmt = (n: number | null): string =>
    n == null ? "?" : n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${Math.round(n / 1_000)}K`;
  let position = "";
  if (currentPrice != null && low != null && high != null) {
    if (currentPrice < low) {
      position = ` — current ask ${fmt(currentPrice)} is below the range`;
    } else if (currentPrice > high) {
      position = ` — current ask ${fmt(currentPrice)} is above the range`;
    } else {
      position = ` — current ask ${fmt(currentPrice)} sits inside the range`;
    }
  }
  const range =
    low != null && high != null && low !== high
      ? `${fmt(low)} – ${fmt(high)}`
      : fmt(low ?? high);
  const confColor: Record<string, string> = {
    low: "var(--bad)",
    medium: "var(--warn)",
    high: "var(--good)",
  };
  return (
    <div style={{ margin: "0 0 12px" }}>
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        Fair value estimate: <strong style={{ color: "var(--text)" }}>{range}</strong>
        {confidence && (
          <span
            style={{
              display: "inline-block",
              marginLeft: 8,
              background: confColor[confidence] ?? "var(--muted)",
              color: "white",
              padding: "1px 7px",
              borderRadius: 8,
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 600,
              verticalAlign: "middle",
            }}
            title={`Model's self-rated confidence: ${confidence}`}
          >
            {confidence} conf
          </span>
        )}
        {position}
      </p>
      {reasoning && (
        <p
          className="muted"
          style={{
            margin: "4px 0 0",
            fontSize: 11,
            fontStyle: "italic",
            lineHeight: 1.4,
            paddingLeft: 8,
            borderLeft: "2px solid var(--border)",
          }}
        >
          {reasoning}
        </p>
      )}
    </div>
  );
}

interface CompListing {
  listing_id: string;
  address?: string;
  town?: string;
  status?: string;
  list_price?: number | null;
  sale_price?: number | null;
  sqft?: number | null;
  beds?: number | null;
  baths?: number | null;
  photos?: string[];
  hero_photo?: string;
}

function CompsUsed({ ids, consideredCount }: { ids: string[]; consideredCount: number | null }) {
  // Reuse the listings query cache (already populated when the user came
  // through MapView). If they navigated to this page directly, fetch it.
  const q = useQuery<{ listings: CompListing[] }>({
    queryKey: ["listings"],
    queryFn: () => apiGet("/api/listings"),
    staleTime: 60_000,
  });
  const byId = new Map<string, CompListing>();
  for (const x of q.data?.listings ?? []) byId.set(x.listing_id, x);

  return (
    <div style={{ marginTop: 14 }}>
      <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
        Comps used
        {consideredCount != null && consideredCount > ids.length
          ? ` (of ${consideredCount} considered)`
          : ""}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 8,
        }}
      >
        {ids.map((id) => (
          <CompCard key={id} id={id} listing={byId.get(id) ?? null} loading={q.isLoading} />
        ))}
      </div>
    </div>
  );
}

function CompCard({ id, listing, loading }: { id: string; listing: CompListing | null; loading: boolean }) {
  const photo = listing?.photos?.[0] ?? listing?.hero_photo;
  const photoSrc = photo ? (photo.startsWith("http") ? photo : `/${photo}`) : null;
  const price = listing?.list_price ?? listing?.sale_price ?? null;
  const statusLabel =
    listing?.status === "sold" ? "Sold" : listing?.status === "pending" ? "Pending" : null;

  return (
    <Link
      to={`/listings/${id}`}
      style={{
        display: "flex",
        gap: 8,
        padding: 6,
        borderRadius: 6,
        border: "1px solid var(--border)",
        background: "var(--bg)",
        textDecoration: "none",
        color: "var(--text)",
      }}
    >
      <div
        style={{
          width: 70,
          height: 52,
          borderRadius: 4,
          overflow: "hidden",
          flexShrink: 0,
          background: "var(--panel)",
          position: "relative",
        }}
      >
        {photoSrc ? (
          <img src={photoSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "var(--muted)", fontSize: 9 }}>
            no photo
          </div>
        )}
        {statusLabel && (
          <span
            style={{
              position: "absolute",
              top: 2,
              left: 2,
              background: listing?.status === "sold" ? "#6b7280" : "#f59e0b",
              color: "white",
              fontSize: 9,
              padding: "0 4px",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {statusLabel}
          </span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.3 }}>
        <div style={{ fontWeight: 600 }}>
          {price != null ? "$" + price.toLocaleString() : loading ? "…" : "—"}
        </div>
        <div className="muted" style={{ fontSize: 11 }}>
          {(listing?.beds ?? "?")} bd · {(listing?.baths ?? "?")} ba ·{" "}
          {listing?.sqft != null ? listing.sqft.toLocaleString() : "?"} sqft
        </div>
        <div
          style={{
            fontSize: 11,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={listing?.address ?? id}
        >
          {listing?.address ?? id}
        </div>
        {listing?.town && (
          <div className="muted" style={{ fontSize: 10 }}>{listing.town}</div>
        )}
      </div>
    </Link>
  );
}

function ProsConsList({ title, items, color }: { title: string; items: string[]; color: string }) {
  return (
    <div>
      <div style={{ fontWeight: 600, color, marginBottom: 4 }}>{title}</div>
      {items.length === 0 ? (
        <span className="muted" style={{ fontSize: 12 }}>None reported.</span>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {items.map((s, i) => (
            <li key={i} style={{ marginBottom: 4, lineHeight: 1.4 }}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MapPanel({ listing: l }: { listing: Listing }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const lat = numOrNull(l.lat);
  const lng = numOrNull(l.lng);

  useEffect(() => {
    if (!containerRef.current || mapRef.current || lat == null || lng == null) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: [lng, lat],
      zoom: 15,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");

    const el = document.createElement("div");
    el.className = "listing-pin";
    new maplibregl.Marker({ element: el, anchor: "bottom" })
      .setLngLat([lng, lat])
      .addTo(map);

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Pan if coords change after first render (rare, but cheap)
  }, [lat, lng]);

  if (lat == null || lng == null) {
    return null;
  }

  return (
    <div className="panel" style={{ marginBottom: 16, padding: 0, overflow: "hidden" }}>
      <div
        ref={containerRef}
        style={{ height: 300, width: "100%" }}
      />
    </div>
  );
}

function Header({ listing: l }: { listing: Listing }) {
  const headlinePrice = l.status === "sold" && l.sale_price ? l.sale_price : l.list_price;
  const statusLabel = {
    active: "For sale",
    pending: "Pending",
    sold: "Sold",
    withdrawn: "Off market",
  }[l.status] ?? l.status;
  const statusClass = l.status === "sold" ? "tag-error" : l.status === "pending" ? "tag-expired" : "tag-active";

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{fmtPriceFull(headlinePrice)}</div>
          <div className="muted" style={{ marginTop: 2 }}>
            <span className={statusClass}>{statusLabel}</span>
            {l.status === "sold" && l.sale_date ? ` · ${l.sale_date}` : ""}
            {l.status === "sold" && l.list_price && l.sale_price && l.list_price !== l.sale_price ? (
              <> · listed {fmtPriceFull(l.list_price)} ({pctDelta(l.list_price, l.sale_price)})</>
            ) : null}
          </div>
          <div style={{ marginTop: 8, fontSize: 16 }}>{l.address}</div>
        </div>
        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
          <a href={l.url} target="_blank" rel="noreferrer">
            View on {l.source} →
          </a>
          <InterestToggle listingId={l.listing_id} interest={l.interest} />
        </div>
      </div>
    </div>
  );
}

// Assessor living area and MLS square footage are measured differently, so a
// backfilled number is labelled rather than passed off as the listing's own.
const ASSESSOR_NOTE = "from county tax records";

function StatsBar({ listing: l }: { listing: Listing }) {
  const stats: { label: string; value: React.ReactNode; note?: string }[] = [
    { label: "Beds", value: l.beds ?? "—" },
    { label: "Baths", value: l.baths ?? "—" },
    {
      label: "Sqft",
      value: l.sqft != null ? l.sqft.toLocaleString() : "—",
      note: l.sqft_source === "modiv" ? ASSESSOR_NOTE : undefined,
    },
    {
      label: "Lot",
      value: fmtAcres(l.lot_size),
      note: l.lot_size_source === "modiv" ? ASSESSOR_NOTE : undefined,
    },
    {
      label: "Year built",
      value: l.year_built ?? "—",
      note: l.year_built_source === "modiv" ? ASSESSOR_NOTE : undefined,
    },
    { label: "Days on market", value: fmtDaysOnMarket(l) },
  ];
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 8 }}>
        {stats.map((s) => (
          <div key={s.label}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {s.value}
              {s.note && (
                <span
                  title={s.note}
                  className="muted"
                  style={{ fontSize: 11, marginLeft: 4, fontWeight: 400 }}
                >
                  †
                </span>
              )}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {stats.some((s) => s.note) && (
        <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          † {ASSESSOR_NOTE} — the listing didn't publish this figure.
        </div>
      )}
    </div>
  );
}

// Feet under a quarter mile, miles above — the units people picture a street in.
function fmtDistance(meters?: number | null): string {
  if (meters == null) return "?";
  if (meters < 400) return `${Math.round((meters * 3.28084) / 10) * 10} ft`;
  return `${(meters / 1609.34).toFixed(1)} mi`;
}

function noiseScoreColor(score: number): string {
  if (score >= 75) return "var(--good)";
  if (score >= 50) return "var(--accent)";
  if (score >= 25) return "#f59e0b";
  return "var(--bad)";
}

// HowLoud scores 50 (very loud) to 100 (very quiet); ours runs the full 0-100.
// Rescaled here only so the two can be compared — their number is never folded
// into ours.
function howLoudComparable(soundscore: number): number {
  return Math.max(0, Math.min(100, (soundscore - 50) * 2));
}

const NOISE_DISAGREEMENT = 25;

function ScoreReadout({ label, score, band }: { label: string; score: number; band?: string }) {
  return (
    <div style={{ flex: 1 }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: noiseScoreColor(score) }}>
        {score}
        <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>/100</span>
      </div>
      {band && <div className="muted" style={{ fontSize: 12, textTransform: "capitalize" }}>{band}</div>}
    </div>
  );
}

function StreetNoisePanel({ listing: l }: { listing: Listing }) {
  const qc = useQueryClient();
  const lookup = useMutation({
    mutationFn: () => apiPost(`/api/listings/${l.listing_id}/noise-lookup`),
    onSuccess: () => {
      // The worker writes asynchronously; refetch shortly after queueing.
      setTimeout(() => qc.invalidateQueries({ queryKey: ["listing", l.listing_id] }), 6000);
    },
  });

  const rec = l.noise ?? {};
  const status = l.noise_status;
  const scored = status === "ok" && l.noise_score != null;
  const front = rec.frontage;

  const roads = (rec.sources ?? []).filter((s) => s.highway).slice(0, 4);
  const howloud = rec.howloud?.soundscore;
  const disagrees =
    scored && howloud != null && Math.abs(howLoudComparable(howloud) - (l.noise_score ?? 0)) > NOISE_DISAGREEMENT;

  const explanation: Record<string, string> = {
    no_coordinates: "This listing has no coordinates, so there is nothing to measure from.",
    no_roads: "No road data came back for these coordinates — worth trying again.",
    error: "The road-data service kept failing on every retry — worth trying again.",
  };

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Street noise &amp; traffic</h3>

      {scored && (
        <>
          <div className="row" style={{ gap: 16, marginBottom: 12 }}>
            <ScoreReadout label="Quiet at the house" score={l.noise_score!} band={rec.noise_band} />
            {l.street_busyness != null && (
              <ScoreReadout label="Calm on your street" score={l.street_busyness} band={rec.busyness_band} />
            )}
          </div>

          {front && (
            <p style={{ margin: "0 0 8px" }}>
              Fronts onto <strong>{front.name}</strong>{" "}
              <span className="muted">({fmtDistance(front.distance_m)} away)</span>
              {front.aadt != null && (
                <>
                  {" — "}
                  {front.aadt.toLocaleString()} vehicles/day{" "}
                  <span className="muted">
                    {front.aadt_source === "njdot"
                      ? `(NJDOT count${front.aadt_year ? ` ${front.aadt_year}` : ""})`
                      : "(estimated from road class — NJDOT doesn't count this road)"}
                  </span>
                </>
              )}
            </p>
          )}

          {roads.length > 0 && (
            <>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Loudest nearby roads</div>
              <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                {roads.map((r, i) => (
                  <li key={`${r.name}-${i}`}>
                    {r.name} <span className="muted">— {fmtDistance(r.distance_m)}, {r.highway}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {rec.rail && (
            <p className="muted" style={{ margin: "0 0 4px" }}>
              Rail line ({rec.rail.name}) {fmtDistance(rec.rail.distance_m)} away.
            </p>
          )}
          {rec.industrial_m != null && (
            <p className="muted" style={{ margin: "0 0 4px" }}>
              Industrial land {fmtDistance(rec.industrial_m)} away.
            </p>
          )}
          {rec.aerodrome && (
            <p className="muted" style={{ margin: "0 0 4px" }}>
              Airfield ({rec.aerodrome.name}) {fmtDistance(rec.aerodrome.distance_m)} away.
            </p>
          )}

          {howloud != null && (
            <p className="muted" style={{ margin: "8px 0 0", fontSize: 12 }}>
              HowLoud Soundscore (independent): {howloud}/100.
              {disagrees && " Disagrees noticeably with our estimate — worth a visit to settle."}
            </p>
          )}

          <p className="muted" style={{ margin: "8px 0 0", fontSize: 12 }}>
            Modelled from road geometry and traffic counts, not measured. It knows nothing about
            terrain, sound walls, or event traffic — treat it as a shortlist filter, not a verdict.
          </p>
        </>
      )}

      {status && !scored && (
        <p className="muted" style={{ marginTop: 0 }}>
          {explanation[status] ?? `Lookup status: ${status}`}
        </p>
      )}
      {!status && (
        <p className="muted" style={{ marginTop: 0 }}>
          Not looked up yet. Estimates how loud this address is and how busy the street it fronts
          onto is, from OpenStreetMap road geometry and NJDOT traffic counts.
        </p>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button onClick={() => lookup.mutate()} disabled={lookup.isPending}>
          {lookup.isPending ? "Queueing…" : status ? "Check again" : "Check noise"}
        </button>
      </div>
      {lookup.isSuccess && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Queued — this panel refreshes in a few seconds.
        </p>
      )}
      {lookup.error && <p className="tag-error" style={{ marginBottom: 0 }}>{String(lookup.error)}</p>}
    </div>
  );
}

const CLIMATE_HAZARDS: [string, string][] = [
  ["flood", "Flood"],
  ["fire", "Fire"],
  ["wind", "Wind"],
  ["heat", "Heat"],
  ["air", "Air quality"],
];

// First Street scores run 1 (minimal) to 10 (extreme); higher is worse.
function climateColor(score: number, max: number): string {
  const frac = score / (max || 10);
  if (frac >= 0.6) return "var(--bad)";
  if (frac >= 0.3) return "var(--warn)";
  return "var(--good)";
}

function FloodRiskPanel({ listing: l }: { listing: Listing }) {
  const qc = useQueryClient();
  const lookup = useMutation({
    mutationFn: () => apiPost(`/api/listings/${l.listing_id}/flood-lookup`),
    onSuccess: () => {
      // The worker writes asynchronously; refetch shortly after queueing.
      setTimeout(() => qc.invalidateQueries({ queryKey: ["listing", l.listing_id] }), 5000);
    },
  });

  const fema = l.flood ?? {};
  const status = l.flood_status;
  const climate = l.climate ?? {};
  const hazards = CLIMATE_HAZARDS.filter(([k]) => climate[k]?.score != null);
  const flood = climate.flood;
  // First Street's own reading of the map, shown when FEMA's lookup hasn't
  // answered yet or when the two disagree.
  const fsZone = flood?.fema_zone;
  const showFsZone =
    !!fsZone && (status !== "ok" || fsZone.toUpperCase() !== (fema.zone ?? "").toUpperCase());

  const explanation: Record<string, string> = {
    no_coordinates: "This listing has no coordinates, so there is nothing to look up.",
    unmapped: "FEMA has no digitised flood map at these coordinates.",
    error: "The FEMA lookup failed — worth trying again.",
  };
  const insurance = (["flood", "fire", "wind"] as const)
    .map((k) => [k, climate[k]?.insurance_recommendation] as const)
    .filter(([, rec]) => !!rec);

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Flood &amp; climate risk</h3>

      {status === "ok" && fema.zone && (
        <div style={{ marginBottom: 12 }}>
          <div className="muted" style={{ fontSize: 12 }}>FEMA flood zone</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: fema.sfha ? "var(--bad)" : "var(--good)" }}>
            {fema.zone}
            {fema.subtype && (
              <span
                className="muted"
                style={{ fontSize: 13, fontWeight: 400, marginLeft: 8, textTransform: "lowercase" }}
              >
                {fema.subtype}
              </span>
            )}
          </div>
          {fema.sfha ? (
            <p style={{ margin: "4px 0 0", color: "var(--bad)" }}>
              <strong>Special Flood Hazard Area.</strong> Flood insurance is required with a federally
              backed mortgage
              {fema.base_flood_elevation_ft != null && <> — base flood elevation {fema.base_flood_elevation_ft} ft</>}.
            </p>
          ) : (
            <p className="muted" style={{ margin: "4px 0 0" }}>
              Outside the Special Flood Hazard Area; flood insurance is optional.
            </p>
          )}
          {!fema.sfha && fema.nearby_sfha_zones && fema.nearby_sfha_zones.length > 0 && (
            <p style={{ margin: "4px 0 0", color: "var(--warn)" }}>
              SFHA zone {fema.nearby_sfha_zones.join("/")} lies within {fema.sfha_within_m ?? 100} m of the
              geocoded point — part of the lot may be in it. Check the flood map or get an elevation
              certificate.
            </p>
          )}
        </div>
      )}
      {status && status !== "ok" && (
        <p className="muted" style={{ marginTop: 0 }}>
          {explanation[status] ?? `FEMA lookup status: ${status}`}
        </p>
      )}
      {!status && <p className="muted" style={{ marginTop: 0 }}>FEMA flood zone not looked up yet.</p>}
      {showFsZone && (
        <p className="muted" style={{ margin: "0 0 8px" }}>
          First Street reads this as FEMA zone <strong>{fsZone}</strong>.
        </p>
      )}

      {hazards.length > 0 && (
        <>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>First Street risk (via Zillow)</div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
            <tbody>
              {hazards.map(([k, label]) => {
                const h = climate[k]!;
                const max = h.max ?? 10;
                return (
                  <tr key={k}>
                    <td style={{ padding: "2px 0" }}>{label}</td>
                    <td style={{ padding: "2px 8px", fontWeight: 600, color: climateColor(h.score!, max) }}>
                      {h.score}/{max}
                    </td>
                    <td className="muted" style={{ padding: "2px 0" }}>{h.label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {insurance.map(([k, rec]) => (
            <p key={k} className="muted" style={{ margin: "0 0 4px", fontSize: 13 }}>
              <span style={{ textTransform: "capitalize" }}>{k}</span> insurance: {rec}
            </p>
          ))}
        </>
      )}
      {l.source === "zillow" && l.climate == null && (
        <p className="muted" style={{ margin: "0 0 4px", fontSize: 12 }}>
          Climate scores arrive with the next Zillow extras fetch.
        </p>
      )}

      <p className="muted" style={{ margin: "8px 0 0", fontSize: 12 }}>
        FEMA zone is from the effective National Flood Hazard Layer at the listing's coordinates; a
        lender's flood determination is final. First Street scores are modelled, 1 (minimal) to 10 (extreme).
      </p>

      <div className="row" style={{ marginTop: 12 }}>
        <button onClick={() => lookup.mutate()} disabled={lookup.isPending}>
          {lookup.isPending ? "Queueing…" : status ? "Check FEMA again" : "Check FEMA zone"}
        </button>
      </div>
      {lookup.isSuccess && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Queued — this panel refreshes in a few seconds.
        </p>
      )}
      {lookup.error && <p className="tag-error" style={{ marginBottom: 0 }}>{String(lookup.error)}</p>}
    </div>
  );
}

function TaxRecordPanel({ listing: l }: { listing: Listing }) {
  const qc = useQueryClient();
  const lookup = useMutation({
    mutationFn: () => apiPost(`/api/listings/${l.listing_id}/modiv-lookup`),
    onSuccess: () => {
      // The worker writes asynchronously; refetch shortly after queueing.
      setTimeout(() => qc.invalidateQueries({ queryKey: ["listing", l.listing_id] }), 4000);
    },
  });

  const rec = l.modiv ?? {};
  const status = l.modiv_status;
  const rows: { label: string; value: string }[] = [];
  if (status === "matched") {
    if (rec.matched_location) rows.push({ label: "Parcel", value: rec.matched_location });
    if (rec.district_town) rows.push({ label: "District", value: `${rec.district_town} (${rec.district ?? "?"})` });
    if (rec.block || rec.lot) rows.push({ label: "Block / lot", value: `${rec.block ?? "?"} / ${rec.lot ?? "?"}` });
    if (rec.square_ft != null) rows.push({ label: "Assessor sqft", value: rec.square_ft.toLocaleString() });
    if (rec.year_built != null) rows.push({ label: "Year built", value: String(rec.year_built) });
    if (rec.acreage != null) rows.push({ label: "Acreage", value: `${rec.acreage} ac` });
    if (rec.property_class) rows.push({ label: "Class", value: rec.property_class });
    if (rec.style) rows.push({ label: "Style", value: rec.style });
    if (rec.zone) rows.push({ label: "Zone", value: rec.zone });
    if (rec.last_sale_price) {
      rows.push({
        label: "Last recorded sale",
        value: `$${rec.last_sale_price.toLocaleString()}${rec.last_sale_date ? ` · ${rec.last_sale_date}` : ""}`,
      });
    }
  }

  const explanation: Record<string, string> = {
    not_found: "No parcel in this town matched the address.",
    ambiguous: `${rec.candidate_count ?? "Several"} parcels matched — not filled in to avoid using the wrong one.`,
    skipped: rec.reason ?? "Address has no house number to search on.",
  };

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>County tax record</h3>
      {rows.length > 0 && (
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 6, columnGap: 12, margin: 0 }}>
          {rows.map((r) => (
            <div key={r.label} style={{ display: "contents" }}>
              <dt className="muted">{r.label}</dt>
              <dd style={{ margin: 0 }}>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {status && status !== "matched" && (
        <p className="muted" style={{ marginTop: 0 }}>
          {explanation[status] ?? `Lookup status: ${status}`}
        </p>
      )}
      {!status && (
        <p className="muted" style={{ marginTop: 0 }}>
          Not looked up yet. The county publishes assessor square footage, year built and acreage
          for every parcel — useful when the listing leaves them blank.
        </p>
      )}
      <div className="row" style={{ marginTop: 12 }}>
        <button onClick={() => lookup.mutate()} disabled={lookup.isPending}>
          {lookup.isPending ? "Queueing…" : status ? "Look up again" : "Look up records"}
        </button>
        {rec.detail_url && (
          <a href={rec.detail_url} target="_blank" rel="noreferrer">
            View parcel card →
          </a>
        )}
      </div>
      {lookup.isSuccess && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Queued — this panel refreshes in a few seconds.
        </p>
      )}
      {lookup.error && <p className="tag-error" style={{ marginBottom: 0 }}>{String(lookup.error)}</p>}
    </div>
  );
}

function VisitsPanel({ listingId }: { listingId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Visit | null>(null);

  const visitsQ = useQuery<{ visits: Visit[] }>({
    queryKey: ["listing-visits", listingId],
    queryFn: () => apiGet(`/api/listings/${listingId}/visits`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["listing-visits", listingId] });
    qc.invalidateQueries({ queryKey: ["visits"] });
  };

  const create = useMutation({
    mutationFn: (draft: VisitDraft) => apiPost("/api/visits", draft),
    onSuccess: () => { setAdding(false); invalidate(); },
  });
  const update = useMutation({
    mutationFn: ({ visitId, draft }: { visitId: string; draft: VisitDraft }) =>
      apiPut(`/api/visits/${visitId}`, draft),
    onSuccess: () => { setEditing(null); invalidate(); },
  });
  const remove = useMutation({
    mutationFn: (visitId: string) => apiDelete(`/api/visits/${visitId}`),
    onSuccess: invalidate,
  });

  const visits = visitsQ.data?.visits ?? [];

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: visits.length || adding ? 12 : 0 }}>
        <h3 style={{ margin: 0, flex: 1 }}>
          Our visits{visits.length > 0 ? ` (${visits.length})` : ""}
        </h3>
        <button onClick={() => { setAdding((v) => !v); setEditing(null); }}>
          {adding ? "Cancel" : "Log a visit"}
        </button>
      </div>

      {adding && (
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14, marginBottom: 14 }}>
          <VisitForm
            listings={[]}
            lockedListingId={listingId}
            submitting={create.isPending}
            error={create.error ? String(create.error) : null}
            onSubmit={(draft) => create.mutate(draft)}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      {visitsQ.isLoading && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
      {!visitsQ.isLoading && visits.length === 0 && !adding && (
        <p className="muted" style={{ margin: 0 }}>
          No showings logged for this house yet.
        </p>
      )}

      {visits.map((visit) =>
        editing?.visit_id === visit.visit_id ? (
          <div
            key={visit.visit_id}
            style={{ borderTop: "1px solid var(--border)", paddingTop: 14, marginTop: 14 }}
          >
            <VisitForm
              listings={[]}
              lockedListingId={listingId}
              initial={visit}
              submitting={update.isPending}
              error={update.error ? String(update.error) : null}
              onSubmit={(draft) => update.mutate({ visitId: visit.visit_id, draft })}
              onCancel={() => setEditing(null)}
            />
          </div>
        ) : (
          <VisitEntry
            key={visit.visit_id}
            visit={visit}
            onEdit={() => { setEditing(visit); setAdding(false); }}
            onDelete={() => remove.mutate(visit.visit_id)}
          />
        ),
      )}
    </div>
  );
}

function VisitEntry({
  visit,
  onEdit,
  onDelete,
}: {
  visit: Visit;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12, marginTop: 12 }}>
      <div className="row">
        <strong>{fmtVisitDate(visit.visited_on)}</strong>
        {visit.rating != null && <Stars value={visit.rating} />}
        <span style={{ flex: 1 }} />
        <SecondaryButton onClick={onEdit}>Edit</SecondaryButton>
        {confirming ? (
          <>
            <SecondaryButton onClick={onDelete} danger>
              Confirm
            </SecondaryButton>
            <SecondaryButton onClick={() => setConfirming(false)}>Keep</SecondaryButton>
          </>
        ) : (
          <SecondaryButton onClick={() => setConfirming(true)}>Delete</SecondaryButton>
        )}
      </div>
      <VisitAnswerChips visit={visit} />
      {visit.notes && (
        <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, marginTop: 10, marginBottom: 0 }}>
          {visit.notes}
        </p>
      )}
    </div>
  );
}

function OfferPanel({ listing: l }: { listing: Listing }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const offerQ = useQuery<{ offer: Offer | null }>({
    queryKey: ["listing-offer", l.listing_id],
    queryFn: () => apiGet(`/api/listings/${l.listing_id}/offer`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["listing-offer", l.listing_id] });
    qc.invalidateQueries({ queryKey: ["visits"] });
  };

  const save = useMutation({
    mutationFn: (draft: OfferDraft) => apiPut(`/api/listings/${l.listing_id}/offer`, draft),
    onSuccess: () => {
      setEditing(false);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/api/listings/${l.listing_id}/offer`),
    onSuccess: () => {
      setConfirming(false);
      invalidate();
    },
  });

  const offer = offerQ.data?.offer ?? null;
  const meta = OFFER_STATUSES.find((s) => s.key === offer?.status);
  const askingPrice = l.list_price ?? l.sale_price ?? null;

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: offer || editing ? 12 : 0 }}>
        <h3 style={{ margin: 0, flex: 1 }}>Our offer</h3>
        {!editing && (
          <button onClick={() => setEditing(true)} style={{ padding: "6px 12px" }}>
            {offer ? "Edit offer" : "Record an offer"}
          </button>
        )}
      </div>

      {editing ? (
        <OfferForm
          initial={offer}
          submitting={save.isPending}
          error={save.error ? String(save.error) : null}
          onSubmit={(draft) => save.mutate(draft)}
          onCancel={() => setEditing(false)}
        />
      ) : offerQ.isLoading ? (
        <p className="muted" style={{ margin: 0 }}>Loading…</p>
      ) : offer ? (
        <>
          <div className="row" style={{ gap: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {offer.amount != null ? `$${offer.amount.toLocaleString()}` : "Amount not recorded"}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                Offered {fmtVisitDate(offer.offered_on)}
              </div>
            </div>
            <div style={{ color: meta?.color ?? "var(--text)", fontWeight: 600 }}>
              {meta?.label ?? offer.status}
            </div>
            {offer.amount != null && askingPrice != null && askingPrice > 0 && (
              <div className="muted" style={{ fontSize: 12 }}>
                {pctDelta(askingPrice, offer.amount)} vs {l.status === "sold" ? "sale" : "list"} price
              </div>
            )}
          </div>
          {offer.notes && (
            <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, marginTop: 10, marginBottom: 0 }}>
              {offer.notes}
            </p>
          )}
          <div className="row" style={{ marginTop: 12 }}>
            {confirming ? (
              <>
                <SecondaryButton onClick={() => remove.mutate()} danger disabled={remove.isPending}>
                  {remove.isPending ? "…" : "Confirm delete"}
                </SecondaryButton>
                <SecondaryButton onClick={() => setConfirming(false)}>Keep</SecondaryButton>
              </>
            ) : (
              <SecondaryButton onClick={() => setConfirming(true)}>
                We didn't offer on this
              </SecondaryButton>
            )}
          </div>
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          No offer recorded on this house.
        </p>
      )}
    </div>
  );
}

function fmtAcres(sqft: number | null): string {
  if (sqft == null) return "—";
  const acres = sqft / 43560;
  // 2 decimals reads cleanly for typical NJ lot sizes (~0.25 to 5+ acres).
  return `${acres.toFixed(2)} ac`;
}

function fmtDaysOnMarket(l: Listing): string {
  // Prefer the server's listed_date — a real on-market date from the source's
  // price history. Then days_on_zillow, adjusted forward by days since our
  // last scrape so the number stays accurate as the cache ages. first_seen_at
  // is only when we found the row, so that fallback is shown as "N+".
  const day = 86400_000;
  const now = Date.now();
  if (l.listed_date) {
    return String(Math.floor((now - new Date(l.listed_date).getTime()) / day));
  }
  if (l.days_on_zillow != null) {
    const lastSeen = l.last_seen_at ? new Date(l.last_seen_at).getTime() : now;
    const drift = Math.max(0, Math.floor((now - lastSeen) / day));
    return String(l.days_on_zillow + drift);
  }
  if (l.first_seen_at) {
    const days = Math.floor((now - new Date(l.first_seen_at).getTime()) / day);
    return `${days}+`;
  }
  return "—";
}

function DescriptionPanel({ description }: { description: string }) {
  const [expanded, setExpanded] = useState(false);
  const truncated = description.length > 600 && !expanded;
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Description</h3>
      <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
        {truncated ? description.slice(0, 600) + "…" : description}
      </p>
      {description.length > 600 && (
        <button onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function PriceHistoryPanel({ history }: { history: PriceHistoryEntry[] }) {
  if (history.length === 0) {
    return (
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Price history</h3>
        <p className="muted">No price history yet.</p>
      </div>
    );
  }
  // Sort newest first
  const sorted = [...history].sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Price history</h3>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Event</th>
            <th style={{ textAlign: "right" }}>Price</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((h, i) => {
            const prev = sorted[i + 1];
            const delta = prev ? pctDelta(prev.price, h.price) : null;
            return (
              <tr key={`${h.date}-${i}`}>
                <td className="muted">{h.date}</td>
                <td>{h.event || "—"}</td>
                <td style={{ textAlign: "right" }}>
                  {fmtPriceFull(h.price)}
                  {delta && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>{delta}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SCORE_COMPONENT_LABELS: Record<string, string> = {
  town: "Town",
  sqft: "Square footage",
  schools: "Schools",
  value: "Value",
  noise: "Street noise",
  amenities: "Amenities",
  garage: "Garage",
  lot_size: "Lot size",
  year_built: "Year built",
  beds: "Bedrooms",
  baths: "Bathrooms",
};

const AMENITY_LABELS: Record<string, string> = {
  fenced_yard: "fenced yard",
  central_air: "central air",
  finished_basement: "finished basement",
  has_basement: "basement",
  fireplace: "fireplace",
  deck_patio: "deck/patio",
  pool: "pool",
  spa: "spa",
  attached_garage: "attached garage",
  waterfront: "waterfront",
  view: "view",
};

function matchScoreColor(score: number): string {
  if (score >= 75) return "var(--good)";
  if (score >= 50) return "var(--accent)";
  if (score >= 25) return "#f59e0b";
  return "var(--bad)";
}

// The listing's actual value for a component, shown under the bar so the
// score is traceable to a fact ("Sqft 85 — 2,556 sqft").
function scoreComponentDetail(key: string, l: Listing): string | null {
  switch (key) {
    case "town":
      return l.town || null;
    case "sqft":
      return l.sqft != null ? `${l.sqft.toLocaleString()} sqft` : "not listed";
    case "schools": {
      const s = l.school_scores ?? {};
      const present = [s.elementary, s.middle, s.high].filter((v): v is number => typeof v === "number");
      return present.length ? `avg ${(present.reduce((a, b) => a + b, 0) / present.length).toFixed(1)}/10` : null;
    }
    case "value": {
      const price = l.list_price ?? l.sale_price;
      if (price == null) return null;
      if (l.ai_fair_price_low != null && l.ai_fair_price_high != null) {
        return `${fmtPriceFull(price)} vs fair ${fmtPriceFull(l.ai_fair_price_low)}–${fmtPriceFull(l.ai_fair_price_high)}`;
      }
      return l.ai_price_assessment && l.ai_price_assessment !== "unknown" ? l.ai_price_assessment : null;
    }
    case "noise": {
      const bands = [l.noise?.noise_band && `${l.noise.noise_band} ambient`, l.noise?.busyness_band && `${l.noise.busyness_band} street`]
        .filter(Boolean)
        .join(", ");
      return bands || null;
    }
    case "amenities": {
      const present = Object.entries(l.amenities ?? {})
        .filter(([, v]) => v)
        .map(([k]) => AMENITY_LABELS[k] ?? k);
      return present.length ? `has: ${present.join(", ")}` : "none listed";
    }
    case "garage":
      return l.garage_spaces != null ? `${l.garage_spaces} space${l.garage_spaces === 1 ? "" : "s"}` : null;
    case "lot_size":
      return l.lot_size != null ? fmtAcres(l.lot_size) : null;
    case "year_built":
      return l.year_built != null ? `built ${l.year_built}` : null;
    case "beds":
      return l.beds != null ? `${l.beds} beds` : null;
    case "baths":
      return l.baths != null ? `${l.baths} baths` : null;
    default:
      return null;
  }
}

function MatchScorePanel({ listing: l }: { listing: Listing }) {
  const breakdown = l.match_score_breakdown ?? {};
  const entries = Object.entries(breakdown).sort(([, a], [, b]) => b.weight - a.weight);

  if (l.match_score == null || entries.length === 0) {
    return (
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Match score</h3>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          Not scored yet. Set up your factor weights and targets in{" "}
          <Link to="/settings?tab=scoring">Settings → Match scoring</Link>, then scores populate
          automatically.
        </p>
      </div>
    );
  }

  const unscored = Object.keys(SCORE_COMPONENT_LABELS).filter((k) => !(k in breakdown));

  return (
    <div className="panel">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h3 style={{ margin: 0 }}>Match score</h3>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: "white",
            background: matchScoreColor(l.match_score),
            padding: "2px 12px",
            borderRadius: 10,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {l.match_score}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 11, margin: "6px 0 10px" }}>
        Weighted average of the factors below —{" "}
        <Link to="/settings?tab=scoring">adjust weights</Link>.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {entries.map(([key, c]) => {
          const pct = Math.round(c.score * 100);
          const detail = scoreComponentDetail(key, l);
          return (
            <div key={key}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span>
                  {SCORE_COMPONENT_LABELS[key] ?? key}
                  <span className="muted" style={{ marginLeft: 6, fontSize: 10 }}>
                    weight {c.weight}
                  </span>
                </span>
                <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{pct}</span>
              </div>
              <div
                style={{
                  height: 5,
                  borderRadius: 3,
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  overflow: "hidden",
                  marginTop: 3,
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: "100%",
                    background: matchScoreColor(pct),
                    borderRadius: 3,
                  }}
                />
              </div>
              {detail && (
                <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>
                  {detail}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {unscored.length > 0 && (
        <p className="muted" style={{ fontSize: 10, margin: "10px 0 0" }}>
          Not scored (no data, no target set, or weight 0):{" "}
          {unscored.map((k) => SCORE_COMPONENT_LABELS[k]).join(", ")}
        </p>
      )}
    </div>
  );
}

function FactsPanel({ listing: l }: { listing: Listing }) {
  const lat = numOrNull(l.lat);
  const lng = numOrNull(l.lng);
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Facts</h3>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 6, columnGap: 12, margin: 0 }}>
        <dt className="muted">Town</dt><dd style={{ margin: 0 }}>{l.town}</dd>
        <dt className="muted">Source</dt><dd style={{ margin: 0 }}>{l.source}</dd>
        <dt className="muted">Status</dt><dd style={{ margin: 0 }}>{l.status}</dd>
        {l.listed_date && (
          <>
            <dt className="muted">Listed</dt>
            <dd style={{ margin: 0 }}>{fmtDate(l.listed_date)}</dd>
          </>
        )}
        <dt className="muted">First seen</dt><dd style={{ margin: 0 }}>{fmtDate(l.first_seen_at)}</dd>
        <dt className="muted">Last seen</dt><dd style={{ margin: 0 }}>{fmtDate(l.last_seen_at)}</dd>
        {lat != null && lng != null && (
          <>
            <dt className="muted">Lat/Lng</dt>
            <dd style={{ margin: 0 }}>{lat.toFixed(4)}, {lng.toFixed(4)}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function ValuationPanel({ listing: l }: { listing: Listing }) {
  const fmt = (n: number | null | undefined): string =>
    n == null ? "—" : "$" + n.toLocaleString();
  const fmtRange = (lo: number | null | undefined, hi: number | null | undefined): string => {
    if (lo == null && hi == null) return "";
    if (lo != null && hi != null) return `${fmt(lo)} – ${fmt(hi)}`;
    return fmt(lo ?? hi);
  };

  const list = l.list_price ?? l.sale_price ?? null;
  const z = l.zestimate ?? null;
  const zRange = fmtRange(l.zestimate_low, l.zestimate_high);
  const tax = l.tax_assessed_value ?? null;
  const ai = fmtRange(l.ai_fair_price_low, l.ai_fair_price_high);

  // Show panel only if at least one anchor is available beyond the list price.
  const anyAnchor = z != null || tax != null || ai;
  if (!anyAnchor) return null;

  const rows: { label: string; value: string; sub?: string; color?: string }[] = [];
  rows.push({ label: l.status === "sold" ? "Sale price" : "List price", value: fmt(list) });
  if (z != null) rows.push({ label: "Zestimate", value: fmt(z), sub: zRange || undefined });
  if (tax != null) rows.push({ label: "Tax assessed", value: fmt(tax) });
  if (ai) rows.push({ label: "AI fair value", value: ai, color: "var(--accent)" });

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Valuation anchors</h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {rows.map((r) => (
          <li
            key={r.label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 12,
              padding: "8px 0",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>{r.label}</span>
            <span style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 600, color: r.color ?? "var(--text)" }}>{r.value}</div>
              {r.sub && (
                <div className="muted" style={{ fontSize: 11 }}>{r.sub}</div>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function SchoolsPanel({ scores, names }: { scores: SchoolScores; names: SchoolNames }) {
  const rows = [
    { label: "Elementary", level: "elementary" as const, v: scores.elementary, name: names.elementary },
    { label: "Middle", level: "middle" as const, v: scores.middle, name: names.middle },
    { label: "High", level: "high" as const, v: scores.high, name: names.high },
  ];
  const hasAny = rows.some((r) => r.v != null || r.name);
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Schools</h3>
      {!hasAny && <p className="muted">No school data yet.</p>}
      {hasAny && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {rows.map((r) => (
            <li
              key={r.level}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "8px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {r.label}
                </div>
                <div
                  style={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={r.name ?? undefined}
                >
                  {r.name ?? <span className="muted" style={{ fontWeight: 400 }}>—</span>}
                </div>
              </div>
              <span style={{ fontWeight: 600, flexShrink: 0 }}>
                {r.v != null ? <ScoreBadge score={r.v} /> : <span className="muted">—</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 8 ? "var(--good)" : score >= 6 ? "var(--accent)" : score >= 4 ? "var(--warn)" : "var(--bad)";
  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 26,
        textAlign: "center",
        padding: "2px 8px",
        borderRadius: 12,
        background: color,
        color: "white",
        fontSize: 12,
      }}
    >
      {score}/10
    </span>
  );
}

function fmtPriceFull(n: number | null | undefined): string {
  if (n == null) return "—";
  return "$" + n.toLocaleString();
}

function pctDelta(from: number, to: number): string {
  const pct = ((to - from) / from) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
